'use client';

import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { getDateName, getOrderDateTimeFromOloTimeWanted } from '@/utils/dateTimeUtils';

import { Arrow } from '@/components/common/Icons/Arrow';
import { BasketResponse } from '@/app/(ordering)/rewards/data/data.type';
import { Button } from '@/components/common/Button/Button';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import { Clock } from '@/components/common/Icons/Clock';
import { DeliveryAddressProps } from '@/app/(ordering)/shop/CartProvider/cartProviderTypes';
import type { DeliveryCurrentView } from './DeliveryPanel';
import { Location } from '@/components/common/Icons/Location';
import { StartOrderAddressCard } from '@/components/ordering/StartOrderAddressCard/StartOrderAddressCard';
import type { deliveryAddressProps } from '../../data/shopdata.type';
import { sendGAEvent } from '@next/third-parties/google';

type DeliverySelectionProps = {
	closeModal: Dispatch<SetStateAction<void>>;
	setCurrentView: (view: DeliveryCurrentView) => void;
	isAddressAvailable: boolean;
	deliveryAddress: deliveryAddressProps | undefined;
	setDeliveryAddress: (addr: deliveryAddressProps | undefined) => void;
};

export function DeliverySelection({
	closeModal,
	setCurrentView,
	isAddressAvailable,
	deliveryAddress,
	setDeliveryAddress,
}: DeliverySelectionProps) {
	const { setNewAddress, address, apiCart, tempCart } = useContext(CartContext);
	const [estimatedTime, setEstimatedTime] = useState<{
		displayTime: string;
		estTime: string;
	}>({
		displayTime: '',
		estTime: '',
	});
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (isAddressAvailable) {
			const cart: BasketResponse | undefined = tempCart ?? apiCart;
			if (!cart || !cart.formattedOrderTime) {
				return;
			}
			let estTime = cart.formattedOrderTime.split(' - ')[1];

			const timeArr = estTime.split(' ');
			if (timeArr[timeArr.length - 1] === 'am' || timeArr[timeArr.length - 1] === 'pm') {
				timeArr[timeArr.length - 1] = timeArr[timeArr.length - 1].toUpperCase();
			}
			estTime = timeArr.join(' ');

			const disTime = getOrderDateTimeFromOloTimeWanted(cart);
			setEstimatedTime({
				displayTime: `${getDateName(disTime.orderDate)}, ${disTime.orderTime}`,
				estTime: estTime,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAddressAvailable, apiCart]);

	const navigateToRecentSearch = (newDeliveryAddress: deliveryAddressProps) => {
		setDeliveryAddress(newDeliveryAddress);
		setCurrentView('searching');
	};
	const navigateToGuestSearch = () => {
		setCurrentView('searching');
	};

	const startDeliveryOrder = async (finalDeliveryAddress: deliveryAddressProps) => {
		const deliveryAdd: DeliveryAddressProps = {
			secondaryAddress: finalDeliveryAddress.lastDeliveryAddress.building ?? undefined,
			streetAddress: finalDeliveryAddress.lastDeliveryAddress.streetAddress,
			city: finalDeliveryAddress.lastDeliveryAddress.city,
			state: finalDeliveryAddress.lastDeliveryAddress.state,
			zipCode: finalDeliveryAddress.lastDeliveryAddress.zipCode,
			latitude: finalDeliveryAddress.latitude ?? 0,
			longitude: finalDeliveryAddress.longitude ?? 0,
			specialInstructions: finalDeliveryAddress.lastDeliveryAddress.specialInstructions,
		};
		return await setNewAddress({
			type: 'delivery',
			deliveryAddress: deliveryAdd,
			pickupAddress: null,
			orderDateTime: address.orderDateTime,
		});
	};

	return (
		<>
			<div className="my-8 grid grid-rows-1 gap-2">
				<StartOrderAddressCard
					leftIcon={<Location className="icon" />}
					label="DELIVERY ADDRESS"
					title={
						isAddressAvailable ? deliveryAddress?.lastDeliveryAddress?.streetAddress : undefined
					}
					missingAddr={isAddressAvailable ? undefined : 'Please Select An Address'}
					addressChildren={
						isAddressAvailable ? (
							<p className="text-sm" data-ink="light" data-type-style="body-3">
								{deliveryAddress?.lastDeliveryAddress?.city},{' '}
								{deliveryAddress?.lastDeliveryAddress?.zipCode}
							</p>
						) : (
							<></>
						)
					}
					rightIcon={
						<Button
							variant="text"
							className="flex w-full justify-between"
							onClick={() => {
								if (deliveryAddress) {
									navigateToRecentSearch(deliveryAddress);
								} else {
									navigateToGuestSearch();
								}
							}}
						>
							<Arrow className="icon ml-auto self-center" />
						</Button>
					}
					ctaAction={() => {
						if (deliveryAddress) {
							navigateToRecentSearch(deliveryAddress);
						} else {
							navigateToGuestSearch();
						}
					}}
				/>
				<StartOrderAddressCard
					disabled={!isAddressAvailable}
					leftIcon={<Clock />}
					label="DELIVERY TIME"
					title={isAddressAvailable ? estimatedTime.displayTime : ''}
					addressChildren={
						<p className="text-sm" data-ink="light" data-type-style="body-3">
							Estimated Time: {isAddressAvailable ? estimatedTime.estTime : ''}
						</p>
					}
					ctaAction={() => {
						if (deliveryAddress) {
							setCurrentView('dateTime');
						}
					}}
					rightIcon={
						<Button
							variant="text"
							className="flex w-full justify-between"
							onClick={() => {
								if (deliveryAddress) {
									setCurrentView('dateTime');
								}
							}}
						>
							<Arrow className="icon ml-auto self-center" />
						</Button>
					}
				/>
			</div>
			<p className="text-center text-sm" data-ink="light" data-type-style="body-3">
				{`Ordering More Than 6 Dozen? `}
				<a
					className="underline underline-offset-1"
					href={`https://www.ezcater.com/brand/pvt/krispy-kreme`}
					data-ink="link"
					target="_blank"
					rel="noreferrer"
				>
					{`Switch to Catering`}
				</a>
			</p>
			<div className="mx-auto grid w-full max-w-xs grid-cols-1 gap-6 py-8">
				<Button
					data-type-style="button-mobile"
					variant="primary"
					size="large"
					isLoading={isLoading}
					disabled={isLoading || !isAddressAvailable}
					onClick={async () => {
						setIsLoading(true);
						// Track the Start delivery order button click
						if (typeof window !== 'undefined') {
							window.dataLayer = window.dataLayer || [];
							const eventData = {
								event: 'begin_checkout',
								eventModel: {
									type: 'button',
									action: 'click',
									location: 'delivery selection',
									text: 'Start delivery order',
									pathname: window.location.pathname,
									domain: window.location.hostname,
								},
							};
							window.dataLayer.push(eventData);
						}
						if (deliveryAddress) {
							await startDeliveryOrder(deliveryAddress);

							const eventData = {
								value: apiCart?.subtotal,
								currency: 'USD',
								tax: apiCart?.taxes.reduce((previousVal, taxItem) => taxItem.tax + previousVal, 0),
								shipping: apiCart?.deliveryFee,
								zipcode: apiCart?.restaurant.zip,
								storeNumber: apiCart?.storeNumber,
							};
							sendGAEvent('event', 'delivery_order_started', { ...eventData });
							setIsLoading(false);
							closeModal();
						} else {
							setIsLoading(false);
						}
					}}
				>
					Start delivery order
				</Button>
			</div>
		</>
	);
}
